import React from 'react';
import { styled } from '@mui/material';
import type { ResourceBaseCardVariant } from '../ResourceBaseCard/ResourceBaseCard';
import { ResourceBaseCard } from '../ResourceBaseCard/ResourceBaseCard';
import claimsThumbnail from '../../assets/images/ResourcesBenefits/ClaimsThumbnail.png';

const DEFAULT_BENEFIT_IMAGE_URL = claimsThumbnail;

const BenefitImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

export interface BenefitCardProps {
  title: string;
  /** Full-color benefit image URL from the backend. */
  imageUrl?: string;
  /** Background tint behind the image. */
  backgroundColor: string;
  /** Image URL shown when imageUrl is missing or fails to load. Defaults to the Claims thumbnail. */
  imageFallback?: string;
  /** Accessible alt text; empty string keeps the image decorative. */
  imageAlt?: string;
  variant?: ResourceBaseCardVariant;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel?: string;
}

export const BenefitCard: React.FC<BenefitCardProps> = ({
  title,
  imageUrl,
  backgroundColor,
  imageFallback = DEFAULT_BENEFIT_IMAGE_URL,
  imageAlt = '',
  variant = 'square',
  onClick,
  disabled = false,
  selected = false,
  ariaLabel,
}) => {
  // the last image that failed to load
  const [failedSrc, setFailedSrc] = React.useState<string | undefined>(undefined);

  const resolvedImageSrc = imageUrl && imageUrl !== failedSrc ? imageUrl : imageFallback;

  const icon = (
    <BenefitImage
      src={resolvedImageSrc}
      alt={imageAlt}
      aria-hidden={imageAlt === '' ? true : undefined}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (resolvedImageSrc !== imageFallback) {
          setFailedSrc(resolvedImageSrc);
        }
      }}
    />
  );

  return (
    <ResourceBaseCard
      title={title}
      icon={icon}
      backgroundColor={backgroundColor}
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      selected={selected}
      ariaLabel={ariaLabel}
      testId="benefit-topic-card"
      className="benefit-topic-card"
    />
  );
};

export default BenefitCard;
